import React from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import CustomInput from "../formik/CustomInput.tsx";
import CustomTextArea from "../formik/CustomTextArea.tsx";
import { createFaq, updateFaq, Faq } from "../../services/faqs.service.ts";
import { notification } from "../../utils/Notifications.tsx";
import Loader from "../Loader.tsx";

interface FaqFormProps {
  close: () => void;
  onSuccess: () => void;
  faqToEdit?: Faq | null;
  isEditing?: boolean;
}

interface FaqFormValues {
  question: string;
  answer: string;
  mediaLink: string;
  mediaFile: File | null;
}

const FaqForm: React.FC<FaqFormProps> = ({
  close,
  onSuccess,
  faqToEdit,
  isEditing = false,
}) => {
  const [loading, setLoading] = React.useState(false);
  const [selectedFile, setSelectedFile] = React.useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = React.useState<string | null>(
    faqToEdit?.imageUrl || null
  );

  const validationSchema = Yup.object({
    question: Yup.string()
      .required("La pregunta es requerida")
      .min(5, "La pregunta debe tener al menos 5 caracteres")
      .max(500, "La pregunta no puede exceder 500 caracteres"),
    answer: Yup.string()
      .required("La respuesta es requerida")
      .min(10, "La respuesta debe tener al menos 10 caracteres")
      .max(5000, "La respuesta no puede exceder 5000 caracteres"),
    mediaLink: Yup.string()
      .url("Debe ser una URL válida")
      .max(500, "El enlace no puede exceder 500 caracteres"),
  });

  const formik = useFormik<FaqFormValues>({
    initialValues: {
      question: faqToEdit?.question || "",
      answer: faqToEdit?.answer || "",
      mediaLink: faqToEdit?.mediaLink || "",
      mediaFile: null,
    },
    validationSchema,
    onSubmit: async (values) => {
      setLoading(true);

      try {
        const payload = {
          question: values.question,
          answer: values.answer,
          mediaLink: values.mediaLink || undefined,
        };

        if (isEditing && faqToEdit) {
          await updateFaq(faqToEdit.id, payload, selectedFile || undefined);
          notification("", "FAQ actualizada exitosamente", "success");
        } else {
          await createFaq(payload, selectedFile || undefined);
          notification("", "FAQ creada exitosamente", "success");
        }

        onSuccess();
      } catch (error: any) {
        notification(
          "Error ⚠️",
          `Error al ${isEditing ? "actualizar" : "crear"} la FAQ: ${
            error?.message || "Error desconocido"
          }`,
          "danger"
        );
      } finally {
        setLoading(false);
      }
    },
  });

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewUrl(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    setPreviewUrl(faqToEdit?.imageUrl || null);
    formik.setFieldValue("mediaFile", null);
  };

  return (
    <React.Fragment>
      <Loader isLoading={loading} />

      <div className="mb-4">
        <div className="alert alert-info" role="alert">
          <i className="fa fa-info-circle me-2"></i>
          <strong>Información:</strong> Completa la pregunta y respuesta.
          Opcionalmente puedes agregar una imagen o un enlace multimedia.
        </div>
      </div>

      <CustomInput
        formik={formik}
        field="question"
        label="Pregunta"
        placeholder="Ej: ¿Cómo puedo consultar mi expediente?"
        required
        sm={9}
        smLabel={3}
      />

      <CustomTextArea
        field="answer"
        formik={formik}
        label="Respuesta"
        placeholder="Escribe la respuesta detallada a la pregunta..."
        smLabel={3}
        sm={9}
        required
        classInput="mt-3"
      />

      {/* <CustomInput
        classInput="mt-3"
        formik={formik}
        field="mediaLink"
        label="Enlace multimedia"
        placeholder="https://ejemplo.com/video"
        required={false}
        sm={9}
        smLabel={3}
      /> */}

      <div className="row fs-13px col-md-12 mt-3">
        <label className="form-label col-form-label col-md-3 text-end">
          Archivo multimedia
        </label>
        <div className="col-md-9">
          <input
            type="file"
            className="form-control mb-5px fs-13px"
            accept="image/*,video/*"
            onChange={handleFileChange}
          />
          <small className="text-muted">
            Puedes subir una imagen o video (opcional)
          </small>
        </div>
      </div>

      <div className="d-flex justify-content-end mt-4">
        <button
          className="btn btn-secondary me-2"
          onClick={close}
          disabled={loading}
        >
          Cancelar
        </button>
        <button
          className="btn btn-success"
          onClick={() => formik.handleSubmit()}
          disabled={loading}
        >
          {isEditing ? "Actualizar" : "Guardar"} FAQ
        </button>
      </div>
    </React.Fragment>
  );
};

export default FaqForm;
